"use client";

import { createContext, useContext, useEffect, useState } from "react";
import { useRouter } from "next/navigation";

import { getToken, isUnauthorized } from "@/utils/auth";
import { apiRequest } from "@/lib/api";
import { useAuthSessionSync } from "@/lib/use-auth-token-sync";
import { User } from "@/lib/types";
import { LoadFailed } from "@/components/auth/load-failed";

export const AuthContext = createContext<User | null>(null);

export function useAuth() {
  return useContext(AuthContext);
}

type RequireAuthProps = {
  children: React.ReactNode;
};

export default function RequireAuth({ children }: RequireAuthProps) {
  const router = useRouter();
  const [user, setUser] = useState<User | null>(null);
  const [isChecking, setIsChecking] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;

    const checkAuth = async (retriedStale = false): Promise<void> => {
      if (!getToken()) {
        router.push("/login");
        return;
      }

      try {
        const data = await apiRequest("get", "/me/");
        if (cancelled) return;
        setUser(data as User);
        setLoadFailed(false);
      } catch (err) {
        if (cancelled) return;
        if (isUnauthorized(err)) {
          // lib/api.ts already settled/cleared the token; if another tab put a
          // new one in meanwhile, try once with it.
          if (getToken() && !retriedStale) return checkAuth(true);
          router.push("/login");
          return;
        }
        // Network error / 5xx: keep the session, let the user retry.
        setLoadFailed(true);
      }

      setIsChecking(false);
    };

    checkAuth();
    return () => {
      cancelled = true;
    };
  }, [router, reloadKey]);

  useAuthSessionSync({
    user,
    onUser: (data) => {
      setUser(data);
      setLoadFailed(false);
    },
    onLogout: () => router.push("/login"),
  });

  if (isChecking) return null;

  if (loadFailed) {
    return (
      <LoadFailed
        onRetry={() => {
          setIsChecking(true);
          setLoadFailed(false);
          setReloadKey((k) => k + 1);
        }}
      />
    );
  }

  return <AuthContext.Provider value={user}>{children}</AuthContext.Provider>;
}
